import { useState } from 'react';
import { CaretDown, DotsSixVertical, Plus, X } from '@phosphor-icons/react';
import {
  MILESTONES_MAX,
  setMilestonesInputSchema,
  TASK_TYPES,
  type MilestoneInput,
  type TaskType,
} from '@mybusinessmate/schema';
import type { MappedApiError } from '@mybusinessmate/api-client';
import { cx } from '../../lib/cx';
import { TASK_TYPE_META } from '../../lib/task-display';
import { MILESTONE_TEMPLATES } from '../../lib/task-milestones';
import { useSetMilestones } from '../../lib/tasks-api';
import { Icon } from '../Icon';
import { Banner } from '../ui/banner';
import { Button } from '../ui/button';
import { textLinkClass } from '../ui/text-link';
import { pillClass } from '../ui/toolbar-controls';
import { ProgressTrack } from './progress-track';
import { Dialog } from '../ui/dialog';
import { IconButton } from '../ui/icon-button';
import { useToast } from '../ui/toast';

export interface MilestoneEditorProps {
  value: MilestoneInput[];
  onChange: (next: MilestoneInput[]) => void;
  /** Field errors keyed by path, e.g. `milestones.1.title`. */
  errors?: Record<string, string>;
  /** Offers the "Template: Post" picker, which swaps in the usual stops for a kind of task. */
  type?: TaskType;
}

/** "Template: Post": a pill over a native select, so the platform picker comes for free. */
function TemplatePicker({ type, onPick }: { type: TaskType; onPick: (type: TaskType) => void }) {
  return (
    <span className="relative inline-flex">
      <span className={cx(pillClass('light'), 'pointer-events-none')}>
        Template: {TASK_TYPE_META[type].short}
        <Icon icon={CaretDown} size="xs" />
      </span>
      <select
        aria-label="Template"
        value={type}
        onChange={(e) => onPick(e.target.value as TaskType)}
        className="absolute inset-0 size-full cursor-pointer opacity-0"
      >
        {TASK_TYPES.map((t) => (
          <option key={t} value={t}>
            {TASK_TYPE_META[t].label}
          </option>
        ))}
      </select>
    </span>
  );
}

/**
 * The named stops on a task's progress track (Figma A05.3 step 3): a preview of the track, then a row per stop with its
 * name, a line saying what it means, and where on the track it sits. Whether a stop is ticked belongs to the task page,
 * so it is carried through here untouched.
 */
export function MilestoneEditor({ value, onChange, errors = {}, type }: MilestoneEditorProps) {
  const set = (index: number, patch: Partial<MilestoneInput>) =>
    onChange(value.map((m, i) => (i === index ? { ...m, ...patch } : m)));

  return (
    <div className="flex flex-col gap-(--mbm-space-4)">
      {type ? (
        <div className="flex justify-end">
          <TemplatePicker type={type} onPick={(next) => onChange(MILESTONE_TEMPLATES[next].map((m) => ({ ...m })))} />
        </div>
      ) : null}
      {value.length > 0 ? (
        <ProgressTrack
          preview
          value={0}
          milestones={value.map((m) => ({
            title: m.title || 'Milestone',
            targetProgress: Number.isFinite(m.targetProgress) ? m.targetProgress : 0,
            completed: false,
          }))}
          interactive={false}
          onCommit={() => {}}
          onToggle={() => {}}
        />
      ) : (
        <p className="text-body-medium text-text-secondary">
          No milestones. The progress track will have no named stops.
        </p>
      )}
      {value.length > 0 ? (
        <ul className="flex flex-col gap-(--mbm-space-2)">
          {value.map((m, index) => (
            <li
              key={index}
              className="flex items-center gap-(--mbm-space-3) rounded-(--mbm-radius-md) border border-border-line p-(--mbm-space-3)"
            >
              <span aria-hidden className="text-text-muted">
                <Icon icon={DotsSixVertical} size="md" />
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-(--mbm-space-0-5)">
                <input
                  aria-label={`Milestone ${index + 1} name`}
                  placeholder="Name"
                  value={m.title}
                  onChange={(e) => set(index, { title: e.target.value })}
                  className="w-full bg-transparent text-label-large text-text-primary outline-none placeholder:text-text-muted focus-visible:shadow-(--mbm-shadow-focus-ring)"
                />
                <input
                  aria-label={`Milestone ${index + 1} description`}
                  placeholder="What this stop means"
                  value={m.description ?? ''}
                  onChange={(e) => set(index, { description: e.target.value })}
                  className="w-full bg-transparent text-body-small text-text-secondary outline-none placeholder:text-text-muted focus-visible:shadow-(--mbm-shadow-focus-ring)"
                />
                {errors[`milestones.${index}.title`] ? (
                  <span role="alert" className="text-body-xsmall text-feedback-error">
                    {errors[`milestones.${index}.title`]}
                  </span>
                ) : null}
              </span>
              <span className="flex h-(--mbm-size-chip-md) w-(--mbm-layout-number-field-width) shrink-0 items-center justify-center rounded-(--mbm-radius-xs) bg-surface-field px-(--mbm-space-2)">
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={100}
                  aria-label={`Milestone ${index + 1} stop, percent`}
                  value={Number.isFinite(m.targetProgress) ? m.targetProgress : ''}
                  onChange={(e) => set(index, { targetProgress: e.target.value === '' ? NaN : Number(e.target.value) })}
                  className="w-full min-w-0 bg-transparent text-right text-label-medium text-text-primary outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
                />
                <span className="pl-px text-label-medium text-text-primary">%</span>
              </span>
              <IconButton
                icon={X}
                variant="ghost"
                iconSize="sm"
                aria-label={`Remove milestone ${index + 1}`}
                onClick={() => onChange(value.filter((_, i) => i !== index))}
              />
            </li>
          ))}
        </ul>
      ) : null}
      {Object.entries(errors)
        .filter(([key]) => key === 'milestones' || /^milestones\.\d+\.targetProgress$/.test(key))
        .map(([key, message]) => (
          <p key={key} role="alert" className="text-body-small text-feedback-error">
            {message}
          </p>
        ))}
      <button
        type="button"
        disabled={value.length >= MILESTONES_MAX}
        onClick={() => onChange([...value, { title: '', targetProgress: 100 }])}
        className={cx(
          textLinkClass,
          'inline-flex items-center gap-(--mbm-space-2) self-start disabled:opacity-(--mbm-opacity-disabled)',
        )}
      >
        <Icon icon={Plus} size="sm" />
        Add milestone
      </button>
    </div>
  );
}

/** Account managers and admins reshape the track from the task page; the API refuses anyone else. */
export function EditMilestonesDialog({
  open,
  onClose,
  taskId,
  type,
  initial,
}: {
  open: boolean;
  onClose: () => void;
  taskId: string;
  type: TaskType;
  initial: MilestoneInput[];
}) {
  const toast = useToast();
  const save = useSetMilestones(taskId);
  const [value, setValue] = useState(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>();

  const submit = async () => {
    setFormError(undefined);
    const parsed = setMilestonesInputSchema.safeParse({ milestones: value });
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [i.path.join('.'), i.message])));
      return;
    }
    setErrors({});
    try {
      await save.mutateAsync(parsed.data.milestones);
      toast({ title: 'Milestones saved' });
      onClose();
    } catch (err) {
      const e = err as Partial<MappedApiError>;
      if (e.kind === 'validation' && e.fieldErrors?.length)
        setErrors(Object.fromEntries(e.fieldErrors.map((f) => [f.field, f.message])));
      else if (e.kind === 'forbidden') setFormError('Only the account manager or an admin can change the milestones.');
      else if (e.status === 409) setFormError(e.message ?? 'This task can’t be changed right now.');
      else setFormError('We couldn’t save the milestones. Please try again.');
    }
  };

  return (
    <Dialog
      size="wide"
      open={open}
      onClose={onClose}
      title="Progress milestones"
      description="The named stops on the progress track. Ticking them is up to the people on the task."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} loading={save.isPending}>
            Save milestones
          </Button>
        </>
      }
    >
      {formError ? <Banner tone="error">{formError}</Banner> : null}
      <MilestoneEditor value={value} onChange={setValue} errors={errors} type={type} />
    </Dialog>
  );
}
